import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { useTranslation } from "react-i18next";
import { getElderFacingRelationship } from "../utils/relationship";

const ElderLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const [loginType, setLoginType] = useState("self");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const successMessage = location.state?.successMessage || "";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${config.API_BASE_URL}${config.PATIENT.LOGIN}`, {
        email,
        password,
        loginType
      });

      const { patientID, token, kinkeeper, profileComplete, registrationSource } = res.data;

      localStorage.setItem("token", token);
      localStorage.setItem("patientID", patientID);
      localStorage.setItem("profileComplete", profileComplete ? "true" : "false");
      localStorage.setItem("registrationSource", registrationSource || "");
      localStorage.removeItem("relationship");
      localStorage.removeItem("kinkeeperName");
      localStorage.removeItem("kinkeeperEmail");

      if (kinkeeper) {
        localStorage.setItem(
          "relationship",
          getElderFacingRelationship(kinkeeper.relationship)
        );
        localStorage.setItem(
          "kinkeeperName",
          `${kinkeeper.firstName} ${kinkeeper.lastName}`
        );
        localStorage.setItem("kinkeeperEmail", kinkeeper.email);
        navigate("/elder-intro");
        return;
      }

      if (!profileComplete) {
        navigate("/elder-profile-setup");
        return;
      }

      navigate("/elder-map");
    } catch (err) {
      setError(err.response?.data?.error || t("elderLogin.failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <section className="page-panel">
        <div className="page-content auth-layout">
          <div className="support-card">
            <div>
              <div className="intro-badge">{t("elderLogin.badge")}</div>
              <h1 className="page-title">{t("elderLogin.title")}</h1>
              <p className="page-subtitle">
                {t("elderLogin.subtitle")}
              </p>
            </div>

            <div className="support-list">
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{t("elderLogin.support1Title")}</div>
                  <p>{t("elderLogin.support1Text")}</p>
                </div>
              </div>
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{t("elderLogin.support2Title")}</div>
                  <p>{t("elderLogin.support2Text")}</p>
                </div>
              </div>
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{t("elderLogin.support3Title")}</div>
                  <p>{t("elderLogin.support3Text")}</p>
                </div>
              </div>
            </div>
          </div>

          <form className="form-card" onSubmit={handleLogin}>
            <div className="form-header">
              <h2>{t("elderLogin.formTitle")}</h2>
              <p>{t("elderLogin.formSubtitle")}</p>
            </div>

            {successMessage && <div className="success-banner">{successMessage}</div>}
            {error && <div className="error-banner">{error}</div>}

            <div className="button-row" style={{ marginTop: 0 }}>
              <button
                className={loginType === "self" ? "warm-button" : "ghost-button"}
                type="button"
                onClick={() => setLoginType("self")}
              >
                {t("elderLogin.selfTab")}
              </button>
              <button
                className={loginType === "kinkeeper" ? "warm-button" : "ghost-button"}
                type="button"
                onClick={() => {
                  setLoginType("kinkeeper");
                  setPassword("");
                }}
              >
                {t("elderLogin.kinkeeperTab")}
              </button>
            </div>

            <div className="form-grid">
              <div className="form-field full">
                <label htmlFor="elder-email">{t("elderLogin.emailLabel")}</label>
                <input
                  id="elder-email"
                  className="text-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("elderLogin.emailPlaceholder")}
                />
              </div>
              {loginType === "self" && (
                <div className="form-field full">
                  <label htmlFor="elder-password">{t("elderLogin.passwordLabel")}</label>
                  <input
                    id="elder-password"
                    className="text-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("elderLogin.passwordPlaceholder")}
                  />
                </div>
              )}
            </div>

            <div className="button-row">
              <button className="warm-button" type="submit" disabled={loading}>
                {loading ? t("elderLogin.submitting") : t("elderLogin.submit")}
              </button>
            </div>

            <p className="simple-note">
              {t("elderLogin.noAccount")}{" "}
              <button
                type="button"
                className="simple-link"
                style={{ border: "none", background: "transparent", padding: 0 }}
                onClick={() => navigate("/elder-register")}
              >
                {t("elderLogin.registerLink")}
              </button>
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ElderLogin;
